import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Circle, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { MapPin, Loader2, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const centerIcon = L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#2563eb;border:3px solid white;box-shadow:0 1px 6px rgba(0,0,0,.5)"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function Recenter({ center, radius }) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(L.latLng(center).toBounds(radius * 2000), { padding: [10, 10] });
  }, [center, radius, map]);
  return null;
}

function ClickToMove({ onMove }) {
  useMapEvents({ click: (e) => onMove([e.latlng.lat, e.latlng.lng]) });
  return null;
}

export const AreaPicker = ({ value, onChange }) => {
  const [text, setText] = useState(value.label);
  const [busy, setBusy] = useState(false);

  useEffect(() => setText(value.label), [value.label]);

  const search = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const g = await api.geocode(text.trim());
      onChange({ ...value, label: text.trim(), center: [g.lat, g.lng] });
    } catch {
      toast.error("Fant ikke stedet – prøv en annen skrivemåte");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="area-picker" className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-4 items-end">
        <div>
          <Label className="text-sm font-semibold">Område / adresse</Label>
          <div className="mt-1.5 flex gap-2">
            <Input
              data-testid="area-search-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="F.eks. Ski, Asker sentrum eller Storgata 1, Oslo"
              className="bg-background"
            />
            <Button data-testid="area-search-button" variant="outline" onClick={search} disabled={busy} className="gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />} Vis på kart
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold">
            Avstand: <span data-testid="radius-value" className="text-primary">{value.radius} km</span>
          </Label>
          <Slider
            data-testid="radius-slider"
            className="mt-3"
            min={1}
            max={50}
            step={1}
            value={[value.radius]}
            onValueChange={([r]) => onChange({ ...value, radius: r })}
          />
        </div>
      </div>
      <div className="relative z-0 rounded-xl overflow-hidden border border-border">
        <MapContainer center={value.center} zoom={11} scrollWheelZoom style={{ height: "280px", width: "100%" }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Recenter center={value.center} radius={value.radius} />
          <ClickToMove onMove={(c) => onChange({ ...value, center: c, label: value.label })} />
          <Marker position={value.center} icon={centerIcon} />
          <Circle center={value.center} radius={value.radius * 1000} pathOptions={{ color: "#2563eb", fillOpacity: 0.1 }} />
        </MapContainer>
      </div>
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <MapPin className="h-3 w-3" /> Søker innen {value.radius} km fra «{value.label}». Klikk i kartet for å flytte senterpunktet.
      </p>
    </div>
  );
};
